<template>
  <div>
    <!-- <load-profile /> -->
    <n-search-container :fields="searchFields" @search="search" @reset="reset">
      <n-search-input ref="search" v-model="params" :fields="searchFields" />
    </n-search-container>

    <b-card no-body class="mb-0">
      <div class="m-2">
        <b-row>
          <b-col>
            <label>{{ $t('general.entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t('selection.noOption') }}
              </template>
            </v-select>
          </b-col>
          <b-col v-if="$can('create', permissionType)" class="text-right">
            <b-button variant="primary" @click="showModal">
              {{ $t('button.create') }}
            </b-button>
          </b-col>
        </b-row>
      </div>

      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          :updatable="false"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(title)="row">
            {{ getLocalizedTitle(row.item.titleTranslate || row.item.title) }}
          </template>
          <template #cell(timeRange)="row">
            <!-- {{ row.item.startTime | formatDate('HH:mm') }} -
            {{ row.item.endTime | formatDate('HH:mm') }} -->
            {{ row.item.startTime }} - {{ row.item.endTime }}
          </template>
          <template #cell(timezone)="row">
            {{ getLocaleTimezone(row.item.timezone, $i18n.locale) }}
          </template>
          <template #front-button="row">
            <b-button
              v-b-tooltip.hover
              :title="$t('general.update')"
              variant="primary"
              pill
              size="sm"
              @click="editItem(row.item)"
            >
              <feather-icon icon="EditIcon" />
            </b-button>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>

    <!-- Form Modal -->
    <b-modal v-model="isModalVisible" :title="modalTitle" hide-footer>
      <time-shift-form
        :data="selectedItem"
        @success="onAddedTimeShift"
        @hidden="onTimeShiftModalClose"
      />
    </b-modal>
  </div>
</template>

<script>
  import {
    BCard,
    BRow,
    BCol,
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTimepicker,
    BFormInvalidFeedback
  } from 'bootstrap-vue';
  import vSelect from 'vue-select';
  import Repository from '@/repositories/RepositoryFactory';
  import NPagination from '@/components/NPagination';
  import NTable from '@/components/NTable';
  import NTimePicker from '@/components/NTimePicker.vue';
  import NSearchContainer from '@/components/NSearchContainer';
  import NSearchInput from '@/components/NSearchInput';
  import tableField from './tableField';
  import SearchInputs from './searchInput';
  import TimeShiftForm from '@/components/shares/TimeShiftForm.vue';
  import { getLocaleTimezone } from '@/utils/translate';

  const TimeShiftRepository = Repository.get('timeShift');

  export default {
    name: 'TimeShiftList',
    components: {
      BCard,
      BRow,
      BCol,
      BButton,
      BModal,
      BForm,
      BFormGroup,
      BFormInput,
      BFormTimepicker,
      vSelect,
      NPagination,
      NTable,
      NTimePicker,
      BFormInvalidFeedback,
      NSearchContainer,
      NSearchInput,
      TimeShiftForm
    },
    data() {
      return {
        items: [],
        loading: false,
        total: 0,
        perPageOptions: this.$store.state.pagination.perPageOptions,
        params: {
          title: this.$route.query.title || null
        },
        query: {
          page: Number(this.$route.query.page) || 1,
          order: this.$route.query.order || null,
          sort: this.$route.query.sort || null,
          title: this.$route.query.title || null
        },
        isModalVisible: false,
        selectedItem: null
      };
    },
    computed: {
      modalTitle() {
        return this.isEditing
          ? this.$t('breadcrumb.updateTimeShift')
          : this.$t('breadcrumb.createTimeShift');
      },
      perPage: {
        get() {
          return this.$store.state.pagination.perPage;
        },
        set(value) {
          this.$store.commit('pagination/SET_PER_PAGE', value);
        }
      },
      activeSearch() {
        return !!this.$route.query.title || !!this.$route.query.isActive;
      }
    },
    watch: {
      perPage(value) {
        this.limit = value;
        this.list(1);
      }
    },
    mounted() {
      this.getData();
    },
    methods: {
      getLocalizedTitle(title) {
        if (typeof title === 'string') {
          return title;
        }
        if (typeof title === 'object' && title !== null) {
          const currentLocale = this.$i18n.locale.split('-')[0];
          return title[currentLocale] || title.en || title.zh || title.km || title.vi || '';
        }
        return '';
      },
      updateQueryString() {
        this.$router.replace({ query: this.query });
      },
      list(page = 1) {
        this.query.page = page;
        this.updateQueryString();
        this.getData();
      },
      sortChanged(sortBy, sortDirection) {
        this.query.order = sortBy;
        this.query.sort = sortDirection;
        this.updateQueryString();
        this.getData();
      },
      getData() {
        this.loading = true;
        this.query.departmentId = this.$route.params.departmentId;
        TimeShiftRepository.list(this.query)
          .then(response => {
            const { list } = response.data.data;
            this.items = [...list];
            this.total = response.data.data.total;
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false;
          });
      },
      showModal() {
        this.isModalVisible = true;
        this.selectedItem = null;
      },
      editItem(item) {
        this.selectedItem = item;
        this.isModalVisible = true;
      },
      confirmDestroy(id, index, data) {
        this.$bvModal
          .msgBoxConfirm(
            this.$t('alert.unableToRevertAfterDelete', {
              item: `#${index} ${this.getLocalizedTitle(data.titleTranslate || data.title)}`
            }),
            {
              title: this.$t('alert.areYouSure'),
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: this.$t('button.yesDelete'),
              cancelTitle: this.$t('button.cancel'),
              cancelVariant: 'outline-secondary',
              hideHeaderClose: false,
              centered: true
            }
          )
          .then(value => {
            if (value) {
              this.destroy(id);
            }
          });
      },
      destroy(id) {
        TimeShiftRepository.destroy(id)
          .then(() => {
            this.getData();
          })
          .catch(() => {})
          .finally(() => {
            this.loading = false;
          });
      },
      async onAddedTimeShift() {
        this.getData();
        this.isModalVisible = false;
      },
      onTimeShiftModalClose() {
        this.isModalVisible = false;
        this.selectedItem = null;
      },
      search() {
        this.query = {
          ...this.query,
          ...this.params
        };
        this.updateQueryString();
        this.getData();
      },
      reset() {
        this.$refs.search.reset();
        this.query = {
          ...this.query,
          ...this.params
        };
        this.updateQueryString();
        this.getData();
      }
    },
    setup() {
      const permissionType = 'manage-time-shift';
      const route = 'time-shift';
      const fields = tableField;
      const searchFields = SearchInputs;
      return {
        permissionType,
        route,
        fields,
        searchFields,
        getLocaleTimezone
      };
    }
  };
</script>
